import { env } from './env';

type Config = {
  deployEnvironment: string;
  adminUrl: string;
  orderManagerUrl: string;
  examManagerUrl: string;
  patientManagerUrl: string;
  apiGateway?: string;
  nvoqWs: string;
  nvoqWsMaximumConnections: number;
  asrPlexWs: string;
  asrPlexVadWs: string;
  segmentAppId: string;
  splitioApiKey: string;
};

type EnvMapping = {
  local: Config;
  development: Config;
  staging: Config;
  production: Config;
  preview: (arg1: string) => Config; // Special case to allow namespaced previews,
  sironaPreview: (arg1: string) => Config; // Special case to allow namespaced previews
};

const OKTETO_NAMESPACE = env.OKTETO_NAMESPACE || '';
export const SIRONA_PREVIEW: string = env.SIRONA_PREVIEW || '';
const envMapping: EnvMapping = {
  local: {
    deployEnvironment: 'local',
    adminUrl: `http://localhost:3042/manage/users`,
    orderManagerUrl: `http://localhost:3042/manage/order`,
    examManagerUrl: `http://localhost:3042/manage/exam`,
    patientManagerUrl: `http://localhost:3042/manage/patient`,
    apiGateway: 'http://localhost:4000',
    nvoqWs: 'wss://test.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 3,
    asrPlexWs: 'ws://localhost:4000/ws/transcribe',
    asrPlexVadWs: 'ws://localhost:4000/ws/vad',
    segmentAppId: 'Y3YGaLukzPdNWPXEjlGjea3aJso2Y0GG',
    splitioApiKey:
      env.NODE_ENV === 'test' || env.LOCAL_SPLITS === 'true'
        ? 'localhost'
        : 'ulc2iijia2jp42b2mf1l600k61c42iufnmav',
  },
  preview: (oktetoNamespace: string) => ({
    deployEnvironment: 'preview',
    adminUrl: `https://admin-${oktetoNamespace}.preview.dev.sironamedical.com/manage/users`,
    orderManagerUrl: `https://admin-${oktetoNamespace}.preview.dev.sironamedical.com/manage/order`,
    examManagerUrl: `https://admin-${oktetoNamespace}.preview.dev.sironamedical.com/manage/exam`,
    patientManagerUrl: `https://admin-${oktetoNamespace}.preview.dev.sironamedical.com/manage/patient`,
    apiGateway: `https://graphql-${oktetoNamespace}.preview.dev.sironamedical.com`,
    nvoqWs: 'wss://test.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 3,
    asrPlexWs: 'wss://asr-plex.dev.sironamedical.com/ws/transcribe',
    asrPlexVadWs: 'wss://asr-plex.dev.sironamedical.com/ws/vad',
    segmentAppId: 'Y3YGaLukzPdNWPXEjlGjea3aJso2Y0GG',
    splitioApiKey: 'ulc2iijia2jp42b2mf1l600k61c42iufnmav',
  }),
  sironaPreview: (sironaPreview: string) => ({
    deployEnvironment: 'preview',
    adminUrl: `https://admin-${sironaPreview}.preview.dev.sironamedical.com/manage/users`,
    orderManagerUrl: `https://admin-${sironaPreview}.preview.dev.sironamedical.com/manage/order`,
    examManagerUrl: `https://admin-${sironaPreview}.preview.dev.sironamedical.com/manage/exam`,
    patientManagerUrl: `https://admin-${sironaPreview}.preview.dev.sironamedical.com/manage/patient`,
    apiGateway: `https://graphql-${sironaPreview}.dev.sironamedical.com`,
    nvoqWs: 'wss://test.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 3,
    asrPlexWs: 'wss://asr-plex.dev.sironamedical.com/ws/transcribe',
    asrPlexVadWs: 'wss://asr-plex.dev.sironamedical.com/ws/vad',
    segmentAppId: 'Y3YGaLukzPdNWPXEjlGjea3aJso2Y0GG',
    splitioApiKey: 'ulc2iijia2jp42b2mf1l600k61c42iufnmav',
  }),
  development: {
    deployEnvironment: 'development',
    adminUrl: 'https://app.dev.sironamedical.com/manage/users',
    orderManagerUrl: 'https://app.dev.sironamedical.com/manage/order',
    examManagerUrl: 'https://app.dev.sironamedical.com/manage/exam',
    patientManagerUrl: 'https://app.dev.sironamedical.com/manage/patient',
    nvoqWs: 'wss://test.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 3,
    asrPlexWs: 'wss://asr-plex.dev.sironamedical.com/ws/transcribe',
    asrPlexVadWs: 'wss://asr-plex.dev.sironamedical.com/ws/vad',
    segmentAppId: 'Y3YGaLukzPdNWPXEjlGjea3aJso2Y0GG',
    splitioApiKey: 'ulc2iijia2jp42b2mf1l600k61c42iufnmav',
  },
  staging: {
    deployEnvironment: 'staging',
    adminUrl: 'https://app.stg.sironamedical.com/manage/users',
    orderManagerUrl: 'https://app.stg.sironamedical.com/manage/order',
    examManagerUrl: 'https://app.stg.sironamedical.com/manage/exam',
    patientManagerUrl: 'https://app.stg.sironamedical.com/manage/patient',
    nvoqWs: 'wss://healthcare.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 7,
    asrPlexWs: 'wss://asr-plex.stg.sironamedical.com/ws/transcribe',
    asrPlexVadWs: 'wss://asr-plex.stg.sironamedical.com/ws/vad',
    segmentAppId: 'fHPBx5PtUtAR3Si5Fr6m8BurpFGS8Grv',
    splitioApiKey: '69fu9r39ra5d4914kd7g27p246i5gsvhn3ko',
  },
  production: {
    deployEnvironment: 'production',
    adminUrl: 'https://app.sironamedical.com/manage/users',
    orderManagerUrl: 'https://app.sironamedical.com/manage/order',
    examManagerUrl: 'https://app.sironamedical.com/manage/exam',
    patientManagerUrl: 'https://app.sironamedical.com/manage/patient',
    nvoqWs: 'wss://healthcare.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 7,
    asrPlexWs: 'wss://asr-plex.prod.sironamedical.com/ws/transcribe',
    asrPlexVadWs: 'wss://asr-plex.prod.sironamedical.com/ws/vad',
    segmentAppId: 'PAwhfXbi6zGSCvgr5I8hC9kF3ZphVj0g',
    splitioApiKey: 'behco42hidesjj8f2n86lco36qvui88fvi7s',
  },
};

const getConfigFromDeployEnv = () => {
  switch (env.DEPLOY_ENV) {
    case 'production':
      return envMapping.production;
    case 'staging':
      return envMapping.staging;
    case 'development':
      return envMapping.development;
    case 'preview':
      return SIRONA_PREVIEW
        ? envMapping.sironaPreview(SIRONA_PREVIEW)
        : envMapping.preview(OKTETO_NAMESPACE);
    default:
      return envMapping.local;
  }
};

const config: Config = getConfigFromDeployEnv();

export function deriveGraphqlOrigin(
  hostname = typeof location !== 'undefined' ? location.hostname : ''
): string {
  // localhost and preview environments (e.g. app-{ns}.preview.dev.sironamedical.com) don't
  // start with 'app.' — fall back to the static config for those.
  if (!hostname.startsWith('app.')) {
    return config.apiGateway ?? '';
  }

  // All real app hostnames share the same transform: app. → graphql.
  const withGraphql = 'https://' + hostname.replace(/^app\./, 'graphql.');

  // Dev/stg hostnames already carry the env segment (app.dev.*, app.us.dev.*, app.stg.*, …)
  // so the simple app. → graphql. replace is sufficient.
  // Everything else is a production hostname (app.sironamedical.com, app.us.sironamedical.com, …)
  // which has no env segment, so we insert 'prod.' before the main domain.
  if (hostname.endsWith('.dev.sironamedical.com') || hostname.endsWith('.stg.sironamedical.com')) {
    return withGraphql;
  } else {
    return withGraphql.replace(/sironamedical\.com$/, 'prod.sironamedical.com');
  }
}

// If a environment variable is provided, we try to parse and use it
const envOrFallback = <T>(env: Nullable<string | T>, fallback: T): T => {
  if (env != null) {
    if (typeof env === 'string') {
      try {
        return JSON.parse(env);
      } catch (e: any) {
        return env as T;
      }
    } else {
      return env;
    }
  }
  return fallback;
};

const graphqlOrigin: string = envOrFallback(env.API_GATEWAY_URL, deriveGraphqlOrigin());
export const API_GATEWAY_URL: string = graphqlOrigin;
export const GRAPHQL_URL: string = envOrFallback(env.GRAPHQL_ENDPOINT, `${graphqlOrigin}/graphql`);
export const ADMIN_URL: string = envOrFallback(env.ADMIN_URL, config.adminUrl);
export const EXAM_MANAGER_URL: string = envOrFallback(env.EXAM_MANAGER_URL, config.examManagerUrl);
export const ORDER_MANAGER_URL: string = envOrFallback(
  env.ORDER_MANAGER_URL,
  config.orderManagerUrl
);
export const PATIENT_MANAGER_URL: string = envOrFallback(
  env.PATIENT_MANAGER_URL,
  config.patientManagerUrl
);

export const GRAPHQL_WS_URL: string = envOrFallback(
  env.GRAPHQL_WS_ENDPOINT,
  `${graphqlOrigin.replace(/^http/, 'ws')}/graphql`
);

export const ASR_PLEX_WS_URL: string = envOrFallback(env.ASR_PLEX_WS_URL, config.asrPlexWs);
export const ASR_PLEX_VAD_WS_URL: string = envOrFallback(
  env.ASR_PLEX_VAD_WS_URL,
  config.asrPlexVadWs
);

export const DEPLOY_ENV: string = config.deployEnvironment;
export const NVOQ_WS_URL: string = envOrFallback(env.NVOQ_WS_URL, config.nvoqWs);
export const NVOQ_WS_MAXIMUM_CONNECTIONS: number = envOrFallback(
  env.NVOQ_WS_MAXIMUM_CONNECTIONS,
  config.nvoqWsMaximumConnections
);
export const SEGMENT_APP_ID = config.segmentAppId;
export const SPLITIO_API_KEY = config.splitioApiKey;
export const REPORTER_DEBUG: string = envOrFallback(env.REPORTER_DEBUG, 'false');
export const DATADOG_APP_VERSION: string = env.GIT_TAG ?? '?.?.?';
export const LOG_LEVEL: string = envOrFallback(env.LOG_LEVEL, 'info');
